import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ICaseTypesService } from "services/CaseTypesService";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { IUserSettingsService } from "services/UserSettingsService";
import { CaseTypeModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

const initialState = {
  CaseTypes: new Array<CaseTypeModel>(),
  LoadingCaseTypes: false,
  EditingCaseTypes: false,
  SavedColumnOrder: new Array<string>(),
};

export const caseTypesSlice = createSlice({
  name: "caseTypes",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getCaseTypes.pending, (state) => {
        state.LoadingCaseTypes = true;
      })
      .addCase(getCaseTypes.fulfilled, (state, action) => {
        state.CaseTypes = action.payload;
        state.LoadingCaseTypes = false;
      })
      .addCase(getCaseTypes.rejected, (state) => {
        state.LoadingCaseTypes = false;
      })
      .addCase(createCaseType.pending, (state) => {
        state.EditingCaseTypes = true;
      })
      .addCase(updateCaseType.pending, (state) => {
        state.EditingCaseTypes = true;
      })
      .addCase(deleteCaseType.pending, (state) => {
        state.EditingCaseTypes = true;
      })
      .addCase(createCaseType.rejected, (state) => {
        state.EditingCaseTypes = false;
      })
      .addCase(updateCaseType.rejected, (state) => {
        state.EditingCaseTypes = false;
      })
      .addCase(deleteCaseType.rejected, (state) => {
        state.EditingCaseTypes = false;
      })
      .addCase(createCaseType.fulfilled, (state) => {
        toast.success("Case Type Created");
        state.EditingCaseTypes = false;
      })
      .addCase(updateCaseType.fulfilled, (state) => {
        toast.success("Case Type Updated");
        state.EditingCaseTypes = false;
      })
      .addCase(deleteCaseType.fulfilled, (state) => {
        toast.success("Case Type Deleted");
        state.EditingCaseTypes = false;
      })
      .addCase(getCaseTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      })
      .addCase(updateCaseTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.meta.arg;
        toast.success("Column order saved");
      });
  },
});

export const getCaseTypes = createAsyncThunk("CaseTypes/GetCaseTypes", async () => {
  const caseTypesService = container.get<ICaseTypesService>(SERVICE_TYPES.CaseTypesService);
  return await caseTypesService.getCaseTypes();
});

export const createCaseType = createAsyncThunk<void, { name: string; showOnPortal: boolean }, { state: RootState }>(
  "CaseTypes/CreateCaseTypes",
  async ({ name, showOnPortal }, thunkAPI) => {
    const caseTypesService = container.get<ICaseTypesService>(SERVICE_TYPES.CaseTypesService);
    await caseTypesService.createCaseType(name, showOnPortal);
    thunkAPI.dispatch(getCaseTypes());
  }
);

export const updateCaseType = createAsyncThunk<void, { caseTypeModel: CaseTypeModel }, { state: RootState }>(
  "CaseTypes/UpdateCaseType",
  async ({ caseTypeModel }, thunkAPI) => {
    const caseTypesService = container.get<ICaseTypesService>(SERVICE_TYPES.CaseTypesService);
    await caseTypesService.updateCaseType(caseTypeModel);
    thunkAPI.dispatch(getCaseTypes());
  }
);

export const deleteCaseType = createAsyncThunk<void, { caseTypeID: number }, { state: RootState }>(
  "CaseTypes/DeleteCaseType",
  async ({ caseTypeID }, thunkAPI) => {
    const caseTypesService = container.get<ICaseTypesService>(SERVICE_TYPES.CaseTypesService);
    await caseTypesService.deleteCaseType(caseTypeID);
    thunkAPI.dispatch(getCaseTypes());
  }
);

export const getCaseTypesTableColumnOrder = createAsyncThunk("CaseTypes/GetCaseTypesTableColumnOrder", async () => {
  const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
  return await userSettingsService.getColumnOrder(SETTING_TYPES.CaseTypesTableColumnKey);
});

export const updateCaseTypesTableColumnOrder = createAsyncThunk(
  "CaseTypes/UpdateCaseTypesTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.CaseTypesTableColumnKey);
  }
);

export const selectCaseTypes = (state: RootState) => {
  return state.caseTypes.CaseTypes;
};
export const selectLoadingCaseTypes = (state: RootState) => {
  return state.caseTypes.LoadingCaseTypes;
};
export const selectEditingCaseTypes = (state: RootState) => {
  return state.caseTypes.EditingCaseTypes;
};

export const selectSavedColumnOrder = (state: RootState) => {
  return state.caseTypes.SavedColumnOrder;
};

export default caseTypesSlice.reducer;
